import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PeriodoContext = createContext(null);

export const usePeriodo = () => {
  const context = useContext(PeriodoContext);
  if (!context) {
    throw new Error('usePeriodo must be used within PeriodoProvider');
  }
  return context;
};

export const PeriodoProvider = ({ children, token }) => {
  const [periodoConfig, setPeriodoConfig] = useState(null);
  const [periodoSelecionado, setPeriodoSelecionado] = useState(null);
  const [loading, setLoading] = useState(true);
  const configCarregada = useRef(false);

  // Calcular período baseado na configuração
  const calcularPeriodo = useCallback((config, dataRef = new Date()) => {
    // Garantir que temos uma configuração válida
    const tipo = config?.tipo || 'mes_civil';
    const diaInicio = config?.dia_inicio || 1;
    const diaFim = config?.dia_fim || 31;
    
    // Se os dias são personalizados (não 1 e 31), usar lógica personalizada independente do tipo
    const usarCicloPersonalizado = (diaInicio !== 1 || (diaFim !== 31 && diaFim !== 30 && diaFim !== 28));
    
    let inicio, fim;
    const ano = dataRef.getFullYear();
    const mes = dataRef.getMonth();
    
    if (tipo === 'mes_civil' && !usarCicloPersonalizado) {
      // Primeiro ao último dia do mês
      inicio = new Date(ano, mes, 1);
      fim = new Date(ano, mes + 1, 0); // Último dia do mês
    } else {
      // Personalizado (ex: dia 21 a dia 20)
      const diaAtual = dataRef.getDate();
      
      if (diaAtual >= diaInicio) {
        // Período atual: diaInicio do mês atual ao diaFim do próximo mês
        inicio = new Date(ano, mes, diaInicio);
        fim = new Date(ano, mes + 1, diaFim);
      } else {
        // Período anterior: diaInicio do mês anterior ao diaFim do mês atual
        inicio = new Date(ano, mes - 1, diaInicio);
        fim = new Date(ano, mes, diaFim);
      }
    }
    
    // Formatar datas
    const formatarData = (d) => d.toISOString().split('T')[0];
    const formatarDescricao = (d) => d.toLocaleDateString('pt-BR');
    
    return {
      data_inicio: formatarData(inicio),
      data_fim: formatarData(fim),
      descricao: `${formatarDescricao(inicio)} a ${formatarDescricao(fim)}`,
      mes_referencia: inicio.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' }),
      config_usada: { tipo, dia_inicio: diaInicio, dia_fim: diaFim }
    };
  }, []);

  // Buscar configuração do período da empresa
  const fetchConfiguracao = useCallback(async () => {
    if (!token) return;
    
    try {
      const res = await fetch(`${API_URL}/api/periodo-apuracao/configuracao`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        if (data && data.tipo) {
          console.log('Configuração de período carregada:', data);
          setPeriodoConfig(data);
          configCarregada.current = true;
          // Calcular período inicial baseado na configuração
          const periodoInicial = calcularPeriodo(data);
          setPeriodoSelecionado(periodoInicial);
        } else {
          // Usar padrão mês civil
          const defaultConfig = { tipo: 'mes_civil', dia_inicio: 1, dia_fim: 31 };
          setPeriodoConfig(defaultConfig);
          const periodoInicial = calcularPeriodo(defaultConfig);
          setPeriodoSelecionado(periodoInicial);
        }
      } else {
        const defaultConfig = { tipo: 'mes_civil', dia_inicio: 1, dia_fim: 31 };
        setPeriodoConfig(defaultConfig);
        const periodoInicial = calcularPeriodo(defaultConfig);
        setPeriodoSelecionado(periodoInicial);
      }
    } catch (err) {
      console.error('Erro ao buscar config período:', err);
      const defaultConfig = { tipo: 'mes_civil', dia_inicio: 1, dia_fim: 31 };
      setPeriodoConfig(defaultConfig);
      const periodoInicial = calcularPeriodo(defaultConfig);
      setPeriodoSelecionado(periodoInicial);
    } finally {
      setLoading(false);
    }
  }, [token, calcularPeriodo]);

  useEffect(() => {
    fetchConfiguracao();
  }, [fetchConfiguracao]);

  // Salvar configuração
  const salvarConfiguracao = async (novaConfig) => {
    try {
      const res = await fetch(`${API_URL}/api/periodo-apuracao/configuracao`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(novaConfig)
      });
      
      if (res.ok) {
        console.log('Nova configuração salva:', novaConfig);
        setPeriodoConfig(novaConfig);
        configCarregada.current = true;
        const novoPeriodo = calcularPeriodo(novaConfig);
        setPeriodoSelecionado(novoPeriodo);
        return true;
      }
      return false;
    } catch (err) {
      console.error('Erro ao salvar config:', err);
      return false;
    }
  };

  // Alterar período manualmente
  const alterarPeriodo = (novoInicio, novoFim) => {
    const inicio = new Date(novoInicio + 'T12:00:00');
    const fim = new Date(novoFim + 'T12:00:00');
    
    setPeriodoSelecionado(prev => ({
      data_inicio: novoInicio,
      data_fim: novoFim,
      descricao: `${inicio.toLocaleDateString('pt-BR')} a ${fim.toLocaleDateString('pt-BR')}`,
      mes_referencia: inicio.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' }),
      personalizado: true,
      // Manter a config usada para navegação futura
      config_usada: prev?.config_usada || periodoConfig
    }));
  };

  // Navegar para período anterior/próximo - USA A CONFIG ATUAL, NÃO O PADRÃO
  const periodoAnterior = () => {
    if (!periodoSelecionado || !periodoConfig) return;
    
    // Usar a configuração atual da empresa, NÃO o padrão
    const configAtual = periodoConfig;
    console.log('Navegando período anterior com config:', configAtual);
    
    const dataRef = new Date(periodoSelecionado.data_inicio + 'T12:00:00');
    
    if (configAtual.tipo === 'mes_civil') {
      // Para mês civil, voltar um mês
      dataRef.setMonth(dataRef.getMonth() - 1);
    } else {
      // Para personalizado, voltar para o período anterior mantendo o ciclo
      dataRef.setMonth(dataRef.getMonth() - 1);
    }
    
    const novoPeriodo = calcularPeriodo(configAtual, dataRef);
    setPeriodoSelecionado(novoPeriodo);
  };

  const proximoPeriodo = () => {
    if (!periodoSelecionado || !periodoConfig) return;
    
    // Usar a configuração atual da empresa, NÃO o padrão
    const configAtual = periodoConfig;
    console.log('Navegando próximo período com config:', configAtual);
    
    const dataRef = new Date(periodoSelecionado.data_fim + 'T12:00:00');
    dataRef.setDate(dataRef.getDate() + 1);
    
    const novoPeriodo = calcularPeriodo(configAtual, dataRef);
    setPeriodoSelecionado(novoPeriodo);
  };

  // Resetar para período padrão (baseado na config da empresa)
  const resetarPeriodo = () => {
    if (!periodoConfig) return;
    const periodoAtual = calcularPeriodo(periodoConfig);
    setPeriodoSelecionado(periodoAtual);
  };

  const value = {
    periodoConfig,
    periodoSelecionado,
    loading,
    salvarConfiguracao,
    alterarPeriodo,
    periodoAnterior,
    proximoPeriodo,
    resetarPeriodo,
    refetch: fetchConfiguracao
  };

  return (
    <PeriodoContext.Provider value={value}>
      {children}
    </PeriodoContext.Provider>
  );
};

export default PeriodoContext;
